import { createContext, ReactNode, useContext, useMemo, useState } from 'react';
import {
  InteractionManager,
  LayoutChangeEvent,
  Platform,
  StyleProp,
  View,
  ViewProps,
  ViewStyle,
} from 'react-native';
import { usePersistFn } from '../../functions/hooks/usePersistFn';
import { FCC } from '../../utils/react';

const Context = createContext<{ width: number; height: number }>(null as any);

const LayoutMeasurer = ({
  style,
  fallback,
  children,
  noFlex,
  onLayout,
  ...props
}: {
  style?: StyleProp<ViewStyle>;
  fallback?: ReactNode;
  children: ReactNode;
  noFlex?: boolean;
  onLayout?: (e: LayoutChangeEvent) => void;
} & ViewProps) => {
  const [size, setSize] = useState<{ width: number; height: number }>();
  const onLayoutChange = usePersistFn((e: LayoutChangeEvent) => {
    const { width, height } = e.nativeEvent.layout;
    if (size?.width !== width || size.height !== height) {
      if (size == null) {
        setSize({ width, height });
      } else {
        if (Platform.OS === 'web') {
          setSize({ width, height });
        } else {
          void InteractionManager.runAfterInteractions(() => {
            setSize({ width, height });
          });
        }
      }
    }
    onLayout?.(e);
  });
  return (
    <View
      onLayout={onLayoutChange}
      style={[noFlex ? {} : { flex: 1 }, style]}
      {...props}
    >
      {size == null ? (
        fallback ?? null
      ) : (
        <LayoutMeasurerSizeProvider width={size.width} height={size.height}>
          {children}
        </LayoutMeasurerSizeProvider>
      )}
    </View>
  );
};

export const useParentLayout = () => {
  return useContext(Context);
};

export const LayoutMeasurerSizeProvider: FCC<{
  width: number;
  height: number;
}> = props => {
  const size = useMemo(
    () => ({ width: props.width, height: props.height }),
    [props.width, props.height],
  );
  return <Context.Provider value={size}>{props.children}</Context.Provider>;
};

export default LayoutMeasurer;
